"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icon";
import { useLocale } from "next-intl";

interface LayerItem {
  id: string;
  tabLabel: string;
  title: string;
  desc: string;
  color: string;
  bgSoft: string;
}

const getLayersData = (locale: string): LayerItem[] => {
  if (locale === "hi") {
    return [
      {
        id: "clinical",
        tabLabel: "नैदानिक (Clinical)",
        title: "क्लिनिकल टाइमलाइन",
        desc: "ओपीडी, आईपीडी और लैब में एबीएचए से जुड़ा एक रोगी का एकीकृत सफर।",
        color: "var(--rose)",
        bgSoft: "var(--rose-soft)",
      },
      {
        id: "operational",
        tabLabel: "संचालन (Operational)",
        title: "अस्पताल संचालन",
        desc: "कतारों, फार्मेसी और बिलिंग का शुरू से अंत तक सुचारू संचालन।",
        color: "var(--amber)",
        bgSoft: "#FEF7E0",
      },
      {
        id: "intelligence",
        tabLabel: "इंटेलिजेंस (Intelligence)",
        title: "एम्बेडेड एआई",
        desc: "आपके दैनिक कार्यप्रवाह में सहयोगी बुकिंग चैट, वाणी वीडियो परामर्श, क्लेमआईक्यू और वॉयस-एमडी।",
        color: "var(--cyan-dark)",
        bgSoft: "var(--cyan-soft)",
      },
      {
        id: "trust",
        tabLabel: "सुरक्षा परत (Trust)",
        title: "अनुपालन और सुरक्षा",
        desc: "मूल प्लेटफॉर्म में एबीडीएम (ABDM), एनएचसीएक्स (NHCX) और आईएसओ 27001 का एकीकरण।",
        color: "var(--ink)",
        bgSoft: "rgba(5, 60, 97, 0.05)",
      },
    ];
  }
  return [
    {
      id: "clinical",
      tabLabel: "Clinical",
      title: "Clinical Timeline",
      desc: "A single patient journey keyed to ABHA across OPD, IPD, and Labs.",
      color: "var(--rose)",
      bgSoft: "var(--rose-soft)",
    },
    {
      id: "operational",
      tabLabel: "Operational",
      title: "Hospital Operations",
      desc: "End-to-end orchestration of queues, pharmacy, and billing.",
      color: "var(--amber)",
      bgSoft: "#FEF7E0",
    },
    {
      id: "intelligence",
      tabLabel: "Intelligence",
      title: "Embedded AI",
      desc: "Sahyogi booking chats, Vaani video consults, ClaimIQ intelligent claims, and Voice-MD living inside your workflows.",
      color: "var(--cyan-dark)",
      bgSoft: "var(--cyan-soft)",
    },
    {
      id: "trust",
      tabLabel: "Trust Layer",
      title: "Compliance & Audit",
      desc: "ABDM, NHCX, and ISO 27001 wired into the core platform.",
      color: "var(--ink)",
      bgSoft: "rgba(5, 60, 97, 0.05)",
    },
  ];
};

const getTranslations = (locale: string) => {
  if (locale === "hi") {
    return {
      liveEmr: "लाइव EMR",
      live: "लाइव",
      liveTranslation: "लाइव अनुवाद",
      secure: "सुरक्षित",
      operations: "संचालन",
      aiAgent: "एआई एजेंट",
      encrypted: "एन्क्रिप्टेड",
      patientVitals: "रोगी के वाइटल्स",
      heartRate: "हृदय गति",
      spo2: "SpO₂ (ऑक्सीजन)",
      temperature: "तापमान",
      bloodPressure: "रक्तचाप",
      respRate: "श्वसन दर",
      bloodGlucose: "रक्त शर्करा",
      abhaTimeline: "ABHA यात्रा टाइमलाइन",
      opdConsult: "OPD परामर्श",
      opdConsultSub: "डॉ. शर्मा ने SOAP नोट्स दर्ज किए",
      labRad: "लैब और रेडियोलॉजी",
      labRadSub: "CBC, चेस्ट एक्स-रे सिंक किए गए",
      rxIssued: "प्रिस्क्रिप्शन जारी",
      rxIssuedSub: "Lipi ने डिजिटल हस्ताक्षर के साथ Rx बनाया",
      ipdAdmission: "IPD प्रवेश",
      ipdAdmissionSub: "नर्सिंग नोट्स और देखभाल योजना लिंक की गई",
      dischargeSummary: "डिस्चार्ज सारांश",
      dischargeSummarySub: "एकीकृत EMR रिकॉर्ड फाइनल किया गया",
      flowPipeline: "मरीज प्रवाह पाइपलाइन",
      reg: "पंजीकरण",
      qms: "कतार (QMS)",
      billing: "बिलिंग",
      pharm: "फार्मेसी",
      qmsLive: "लाइव कतार",
      consulting: "परामर्श जारी",
      nextIn: "अगला नंबर",
      ready: "तैयार",
      ipdBeds: "IPD बेड (92% भरे हुए)",
      wardA: "वार्ड A",
      consultationFee: "डॉक्टर परामर्श:",
      pharmacyDispense: "फार्मेसी दवाएं:",
      disposables: "कंज्यूमबल्स:",
      unifiedTotal: "एकीकृत कुल:",
      voiceLipi: "वॉयस-एमडी और लिपि Rx",
      ocrActive: "OCR सक्रिय",
      sahyogiChat: "सहयोगी मरीज चैट",
      sahyogiMsg1: "नमस्ते, मुझे कल का अपना परामर्श पुनर्निर्धारित करना है।",
      sahyogiMsg2: "बिल्कुल, डॉ. शर्मा के पास सुबह 10:00 बजे और दोपहर 2:30 बजे खाली स्लॉट हैं।",
      sahyogiMsg3: "कृपया सुबह 10:00 बजे का स्लॉट बुक करें।",
      sahyogiMsg4: "हो गया! आपका अपॉइंटमेंट कल सुबह 10:00 बजे के लिए पुनर्निर्धारित कर दिया गया है।",
      risk: "अस्वीकृति जोखिम: 1.2%",
      ramesh: "रमेश कुमार",
      icuStar: "ICU प्रबंधन · स्टार हेल्थ",
      identityCheck: "पहचान और लाभार्थी सत्यापन",
      aadhaarResolved: "आधार बेमेल का समाधान हुआ",
      verified: "सत्यापित",
      procedureMatch: "प्रक्रिया और टैरिफ मिलान",
      icdMapped: "ICD-10 (J45.909) को PMJAY से मैप किया",
      validated: "मान्य",
      vaaniDesc: "वास्तविक समय में ऑडियो अनुवाद और लाइव ट्रांसक्रिप्शन।",
      drSharma: "डॉ. शर्मा",
      patient: "मरीज",
      gateways: "अनुपालन गेटवे",
      abdmGateway: "एबीडीएम गेटवे",
      hieCmActive: "HIE-CM एकीकरण सक्रिय",
      connected: "जुड़ा हुआ",
      claimsTunnel: "एन्क्रिप्टेड क्लेम टनल",
      synced: "सिंक किया गया",
      fhirSchema: "FHIR R4 स्कीما",
      hl7Formats: "HL7 क्लिनिकल डेटा प्रारूप",
      compliant: "अनुपालन",
      rbacTitle: "RBAC एक्सेस कंट्रोल",
      drSen: "डॉ. सेन",
      doctor: "डॉक्टर",
      writeEmr: "अनुरोध: EMR लिखें",
      granted: "स्वीकृत",
      staffRoy: "स्टाफ रॉय",
      clinicalRx: "अनुरोध: क्लिनिकल Rx",
      restricted: "अस्वीकृत",
      ledgerTitle: "ऑडिट लेजर टर्मिनल",
      grantedRaw: "स्वीकृत",
      successRaw: "सफल",
      syncedRaw: "सिंक किया गया",
    };
  }
  return {
    liveEmr: "Live EMR",
    live: "Live",
    liveTranslation: "Live Translation",
    secure: "Secure",
    operations: "Operations",
    aiAgent: "AI Agent",
    encrypted: "Encrypted",
    patientVitals: "Patient Vitals",
    heartRate: "Heart Rate",
    spo2: "SpO₂",
    temperature: "Temperature",
    bloodPressure: "Blood Pressure",
    respRate: "Resp. Rate",
    bloodGlucose: "Blood Glucose",
    abhaTimeline: "ABHA Journey Timeline",
    opdConsult: "OPD Consult",
    opdConsultSub: "Dr. Sharma recorded SOAP notes",
    labRad: "Lab & Radiology",
    labRadSub: "CBC, Chest X-Ray results synced",
    rxIssued: "Prescription Issued",
    rxIssuedSub: "Lipi digitised & e-signed Rx",
    ipdAdmission: "IPD Admission",
    ipdAdmissionSub: "Nursing notes & care plan linked",
    dischargeSummary: "Discharge Summary",
    dischargeSummarySub: "Unified EMR record finalised",
    flowPipeline: "Patient Flow Pipeline",
    reg: "Reg",
    qms: "QMS",
    billing: "Billing",
    pharm: "Pharm",
    qmsLive: "QMS Live Queue",
    consulting: "Consulting",
    nextIn: "Next In",
    ready: "Ready",
    ipdBeds: "IPD Beds (92% Occupied)",
    wardA: "Ward A",
    consultationFee: "Consultation:",
    pharmacyDispense: "Pharmacy Dispense:",
    disposables: "Disposables (Optimal):",
    unifiedTotal: "Unified Total:",
    voiceLipi: "Voice-MD & Lipi Rx",
    ocrActive: "OCR Active",
    sahyogiChat: "Sahyogi Patient Chat",
    sahyogiMsg1: "Hi, I need to reschedule my consultation tomorrow.",
    sahyogiMsg2: "Sure, Dr. Sharma has open slots at 10:00 AM and 2:30 PM.",
    sahyogiMsg3: "Please book the 10:00 AM slot.",
    sahyogiMsg4: "Done! Your appointment is rescheduled to tomorrow 10:00 AM.",
    risk: "Rejection Risk: 1.2%",
    ramesh: "Ramesh Kumar",
    icuStar: "ICU Management · Star Health",
    identityCheck: "Identity & Beneficiary Check",
    aadhaarResolved: "Aadhaar mismatch resolved",
    verified: "Verified",
    procedureMatch: "Procedure & Tariff Match",
    icdMapped: "ICD-10 (J45.909) mapped to PMJAY",
    validated: "Validated",
    vaaniDesc: "Real-time audio translation & live transcription.",
    drSharma: "Dr. Sharma",
    patient: "Patient",
    gateways: "Compliance Gateways",
    abdmGateway: "ABDM Gateway",
    hieCmActive: "HIE-CM Integration Active",
    connected: "Connected",
    claimsTunnel: "Encrypted claims tunnel",
    synced: "Synced",
    fhirSchema: "FHIR R4 Schema",
    hl7Formats: "HL7 clinical data formats",
    compliant: "Compliant",
    rbacTitle: "RBAC Access Controls",
    drSen: "Dr. Sen",
    doctor: "Doctor",
    writeEmr: "Request: Write EMR",
    granted: "Granted",
    staffRoy: "Staff Roy",
    clinicalRx: "Request: Clinical Rx",
    restricted: "Restricted",
    ledgerTitle: "Audit Ledger Terminal",
    grantedRaw: "GRANTED",
    successRaw: "SUCCESS",
    syncedRaw: "SYNCED",
  };
};

export function PlatformHeroAnimation() {
  const locale = useLocale();
  const layersData = getLayersData(locale);
  const t = getTranslations(locale);

  const [activeTab, setActiveTab] = useState(layersData[0].id);
  const [isHovered, setIsHovered] = useState(false);
  const [speaker, setSpeaker] = useState<"doctor" | "patient">("patient");

  useEffect(() => {
    const timer = setInterval(() => {
      setSpeaker((s) => (s === "doctor" ? "patient" : "doctor"));
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setActiveTab((current) => {
        const idx = layersData.findIndex((t) => t.id === current);
        return layersData[(idx + 1) % layersData.length].id;
      });
    }, 4500);
    return () => clearInterval(interval);
  }, [isHovered, layersData]);

  const activeLayer = layersData.find((l) => l.id === activeTab)!;

  return (
    <>
      <style>{`
        .apple-container {
          width: 100%;
          padding: 0 16px;
          display: flex;
          justify-content: center;
          margin-top: -64px; /* Shifted up aggressively to avoid cutoff */
          margin-bottom: 32px;
          position: relative;
          z-index: 10;
        }
        .apple-card {
          width: 100%;
          max-width: 100%;
          height: 550px;
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 30px 60px -12px rgba(11, 23, 51, 0.15), 0 0 0 1px rgba(11, 23, 51, 0.05);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
        }
        .apple-chrome {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          background: rgba(250, 250, 250, 0.95);
          border-bottom: 1px solid rgba(11, 23, 51, 0.06);
        }
        .apple-dots { display: flex; gap: 8px; }
        .apple-dot { width: 12px; height: 12px; border-radius: 50%; }
        .apple-dot.red { background: #FF5F56; border: 1px solid #E0443E; }
        .apple-dot.yel { background: #FFBD2E; border: 1px solid #DEA123; }
        .apple-dot.grn { background: #27C93F; border: 1px solid #1AAB29; }
        
        .apple-title {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--ink-4);
          letter-spacing: 0.05em;
          font-weight: 500;
        }
        
        .apple-capsule-wrap {
          padding: 14px 24px;
          background: #ffffff;
          border-bottom: 1px solid rgba(11, 23, 51, 0.04);
        }
        .apple-capsule {
          display: flex;
          background: var(--bg-2);
          padding: 5px;
          border-radius: 999px;
        }
        .apple-tab {
          flex: 1;
          padding: 8px 12px;
          text-align: center;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          position: relative;
          background: transparent;
          border: none;
          font-family: inherit;
          transition: color 0.3s ease;
        }
        .apple-tab-bg {
          position: absolute;
          inset: 0;
          background: #ffffff;
          border-radius: 999px;
          box-shadow: var(--shadow-sm), 0 0 0 1px rgba(11,23,51,0.04);
          z-index: -1;
        }
        
        .apple-content-wrap {
          flex: 1;
          position: relative;
          background: var(--bg);
        }
        .apple-content {
          position: absolute;
          inset: 0;
          padding: 16px 20px 20px;
          display: flex;
          flex-direction: column;
        }
        .apple-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        .apple-header h2 { font-size: 20px; font-weight: 700; color: var(--ink); margin: 0; }
        .apple-desc { font-size: 13.5px; color: var(--ink-3); margin: 4px 0 0; }
        .apple-badge {
          padding: 5px 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          border: 1px solid;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .apple-badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
          animation: pulse 1.6s ease-in-out infinite;
        }
        @keyframes ekgScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        
        /* Infographics Layout */
        .info-panel {
          flex: 1;
          display: flex;
          gap: 12px;
          overflow: hidden;
        }
        .info-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .info-card {
          background: #ffffff;
          border: 1px solid rgba(11, 23, 51, 0.06);
          border-radius: 14px;
          box-shadow: 0 4px 12px rgba(11,23,51,0.02);
          padding: 12px;
          display: flex;
          flex-direction: column;
        }
        .info-card h4 { font-size: 11.5px; font-weight: 700; color: var(--ink-4); text-transform: uppercase; margin: 0 0 10px; letter-spacing: 0.04em; }

        /* Timeline / Lists */
        .timeline-item {
          display: flex;
          gap: 12px;
          position: relative;
          padding-bottom: 12px;
        }
        .timeline-item::after {
          content: ''; position: absolute; left: 4px; top: 16px; bottom: -4px; width: 1.5px; background: rgba(11,23,51,0.06);
        }
        .timeline-item:last-child::after { display: none; }
        .timeline-dot {
          width: 10px; height: 10px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1.5px rgba(11,23,51,0.1); margin-top: 3px; position: relative; z-index: 2;
        }
        .timeline-text { font-size: 12.5px; color: var(--ink); font-weight: 600; line-height: 1.3; }
        .timeline-sub { font-size: 11px; color: var(--ink-3); margin-top: 2px; }

        /* Progress Bars */
        .prog-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .prog-lbl { width: 95px; font-size: 11.5px; color: var(--ink-3); font-weight: 500; text-align: right; }
        .prog-track { flex: 1; height: 10px; background: #f0f0f0; border-radius: 5px; overflow: hidden; }

        /* Terminal */
        .audit-term {
          background: #111827; border-radius: 12px; padding: 12px 14px; font-family: var(--font-mono); font-size: 10.5px; color: #34d399; display: flex; flex-direction: column; gap: 6px; box-shadow: inset 0 2px 10px rgba(0,0,0,0.5); overflow: hidden; height: 140px;
        }

        @media (min-width: 992px) {
          .apple-container {
            justify-content: flex-end;
            margin-top: -108px;
            padding-right: 0;
            transform: translateX(0px) scale(0.85);
            transform-origin: center center;
          }
        }

        @media (max-width: 768px) {
          .apple-container {
            display: block !important;
            position: relative !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 510px !important; /* Dynamic visual container height flow */
            margin-top: -36px !important; /* Pull up to reduce space above visualizer */
            margin-bottom: 12px !important;
            overflow: hidden !important; /* Hide unscaled elements bounds */
            padding: 0 !important;
          }
          .apple-card {
            position: absolute !important;
            top: 0 !important;
            left: 50% !important;
            width: 680px !important; /* Forced design width */
            max-width: none !important;
            height: 550px !important;
            transform: translate(-50%, 0) scale(0.92) !important;
            transform-origin: top center !important;
            margin: 0 !important;
            box-shadow: 0 15px 30px rgba(11, 23, 51, 0.1) !important;
          }
        }

        @media (max-width: 680px) {
          .apple-container {
            height: 465px !important;
          }
          .apple-card {
            transform: translate(-50%, 0) scale(0.84) !important;
          }
        }

        @media (max-width: 580px) {
          .apple-container {
            height: 415px !important;
          }
          .apple-card {
            transform: translate(-50%, 0) scale(0.75) !important;
          }
        }

        @media (max-width: 480px) {
          .apple-container {
            height: 360px !important;
            margin-top: -48px !important; /* Pull up even more on small screens */
          }
          .apple-card {
            transform: translate(-50%, 0) scale(0.65) !important;
          }
        }

        @media (max-width: 410px) {
          .apple-container {
            height: 305px !important;
          }
          .apple-card {
            transform: translate(-50%, 0) scale(0.55) !important;
          }
        }

        @media (max-width: 360px) {
          .apple-container {
            height: 266px !important;
          }
          .apple-card {
            transform: translate(-50%, 0) scale(0.48) !important;
          }
        }
      `}</style>

      <div 
        className="apple-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="apple-card">
          
          <div className="apple-chrome">
            <div className="apple-dots">
              <div className="apple-dot red" />
              <div className="apple-dot yel" />
              <div className="apple-dot grn" />
            </div>
            <div className="apple-title">chikitsa-os // visualizer</div>
            <div style={{ width: 44 }} />
          </div>

          <div className="apple-capsule-wrap">
            <div className="apple-capsule">
              {layersData.map((layer) => {
                const isActive = activeTab === layer.id;
                return (
                  <button
                    key={layer.id}
                    onClick={() => setActiveTab(layer.id)}
                    className="apple-tab"
                    style={{ color: isActive ? layer.color : "var(--ink-4)" }}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTabPill"
                        className="apple-tab-bg"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span style={{ position: "relative", zIndex: 2 }}>{layer.tabLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="apple-content-wrap">
            <AnimatePresence mode="wait">
              {/* CLINICAL VIEW */}
              {activeTab === "clinical" && (
                <motion.div
                  key="clinical"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="apple-content"
                >
                  <div className="apple-header">
                    <div>
                      <h2>{activeLayer.title}</h2>
                      <p className="apple-desc">{activeLayer.desc}</p>
                    </div>
                    <div className="apple-badge" style={{ background: "var(--rose-soft)", color: "var(--rose)", borderColor: "rgba(204,43,88,0.22)" }}>
                      <span className="apple-badge-dot" style={{ background: "var(--rose)" }} />
                      {t.liveEmr}
                    </div>
                  </div>

                  <div className="info-panel">
                    {/* LEFT COL */}
                    <div className="info-col">
                      <div className="info-card" style={{ flex: 1, padding: 14 }}>
                        <h4>{t.patientVitals}</h4>

                        {/* Improved scrolling EKG */}
                        <div style={{ background: "#fafafa", borderRadius: 10, height: 72, border: "1px solid rgba(11,23,51,0.05)", overflow: "hidden", marginBottom: 14, position: "relative" }}>
                          <svg width="100%" height="100%" viewBox="0 0 400 80" preserveAspectRatio="none" style={{ position: "absolute", inset: 0 }}>
                            <defs>
                              <linearGradient id="ekgFade" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="white" stopOpacity="0" />
                                <stop offset="15%" stopColor="white" stopOpacity="1" />
                                <stop offset="85%" stopColor="white" stopOpacity="1" />
                                <stop offset="100%" stopColor="white" stopOpacity="0" />
                              </linearGradient>
                              <mask id="ekgMask">
                                <rect width="400" height="80" fill="url(#ekgFade)" />
                              </mask>
                            </defs>
                            <g mask="url(#ekgMask)">
                              <g>
                                <animateTransform
                                  attributeName="transform"
                                  type="translate"
                                  from="0 0"
                                  to="-800 0"
                                  dur="4.8s"
                                  repeatCount="indefinite"
                                />
                                <path d="M 0 40 L 50 40 C 54 40, 57 36, 60 36 C 63 36, 66 40, 70 40 L 80 40 C 81 40, 82 44, 83 44 C 85 44, 87 10, 88 10 C 89 10, 91 68, 93 68 C 95 68, 97 40, 98 40 L 110 40 C 114 40, 118 30, 122.5 30 C 127 30, 131 40, 135 40 L 200 40 L 250 40 C 254 40, 257 35, 260 35 C 263 35, 266 40, 270 40 L 282 40 C 283 40, 284 43, 285 43 C 287 43, 289 14, 290 14 C 291 14, 293 65, 295 65 C 297 65, 299 40, 300 40 L 312 40 C 316 40, 320 28, 324.5 28 C 329 28, 333 40, 337 40 L 400 40 L 450 40 C 454 40, 457 37, 460 37 C 463 37, 466 40, 470 40 L 478 40 C 479 40, 480 45, 481 45 C 483 45, 485 6, 486 6 C 487 6, 489 72, 491 72 C 493 72, 495 40, 496 40 L 508 40 C 512 40, 516 32, 520.5 32 C 525 32, 529 40, 533 40 L 600 40 L 650 40 C 654 40, 657 36, 660 36 C 663 36, 666 40, 670 40 L 681 40 C 682 40, 683 44, 684 44 C 686 44, 688 12, 689 12 C 690 12, 692 66, 694 66 C 696 66, 698 40, 699 40 L 710 40 C 714 40, 718 29, 722.5 29 C 727 29, 731 40, 735 40 L 800 40"
                                  fill="none" stroke="var(--rose)" strokeWidth="2.5" strokeLinejoin="round" />
                                <path d="M 800 40 L 850 40 C 854 40, 857 36, 860 36 C 863 36, 866 40, 870 40 L 880 40 C 881 40, 882 44, 883 44 C 885 44, 887 10, 888 10 C 889 10, 891 68, 893 68 C 895 68, 897 40, 898 40 L 910 40 C 914 40, 918 30, 922.5 30 C 927 30, 931 40, 935 40 L 1000 40 L 1050 40 C 1054 40, 1057 35, 1060 35 C 1063 35, 1066 40, 1070 40 L 1082 40 C 1083 40, 1084 43, 1085 43 C 1087 43, 1089 14, 1090 14 C 1091 14, 1093 65, 1095 65 C 1097 65, 1099 40, 1100 40 L 1112 40 C 1116 40, 1120 28, 1124.5 28 C 1129 28, 1133 40, 1137 40 L 1200 40 L 1250 40 C 1254 40, 1257 37, 1260 37 C 1263 37, 1266 40, 1270 40 L 1278 40 C 1279 40, 1280 45, 1281 45 C 1283 45, 1285 6, 1286 6 C 1287 6, 1289 72, 1291 72 C 1293 72, 1295 40, 1296 40 L 1308 40 C 1312 40, 1316 32, 1320.5 32 C 1325 32, 1329 40, 1333 40 L 1400 40 L 1450 40 C 1454 40, 1457 36, 1460 36 C 1463 36, 1466 40, 1470 40 L 1481 40 C 1482 40, 1483 44, 1484 44 C 1486 44, 1488 12, 1489 12 C 1490 12, 1492 66, 1494 66 C 1496 66, 1498 40, 1499 40 L 1510 40 C 1514 40, 1518 29, 1522.5 29 C 1527 29, 1531 40, 1535 40 L 1600 40"
                                  fill="none" stroke="var(--rose)" strokeWidth="2.5" strokeLinejoin="round" />
                              </g>
                            </g>
                          </svg>
                        </div>

                        {/* Vitals Grid: 6 metrics */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          {[
                            { label: t.heartRate, value: "72", unit: "BPM" },
                            { label: t.spo2, value: "98", unit: "%" },
                            { label: t.temperature, value: "98.4", unit: "°F" },
                            { label: t.bloodPressure, value: "120/80", unit: "mmHg" },
                            { label: t.respRate, value: "18", unit: "BrPM" },
                            { label: t.bloodGlucose, value: "104", unit: "mg/dL" },
                          ].map((v, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: i * 0.08 }}
                              style={{ background: "var(--rose-soft)", padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(204,43,88,0.08)" }}
                            >
                              <div style={{ fontSize: 9.5, color: "var(--rose)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{v.label}</div>
                              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)", lineHeight: 1.2, marginTop: 2 }}>
                                {v.value} <span style={{ fontSize: 9.5, color: "var(--ink-3)", fontWeight: 500 }}>{v.unit}</span>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COL */}
                    <div className="info-col">
                      <div className="info-card" style={{ flex: 1 }}>
                        <h4>{t.abhaTimeline}</h4>
                        {[
                          { label: t.opdConsult, sub: t.opdConsultSub, done: true },
                          { label: t.labRad, sub: t.labRadSub, done: true },
                          { label: t.rxIssued, sub: t.rxIssuedSub, done: true },
                          { label: t.ipdAdmission, sub: t.ipdAdmissionSub, done: true },
                          { label: t.dischargeSummary, sub: t.dischargeSummarySub, done: false },
                        ].map((item, i) => (
                          <div key={i} className="timeline-item">
                            <motion.div
                              className="timeline-dot"
                              style={{ background: item.done ? "var(--rose)" : "rgba(204,43,88,0.25)" }}
                              animate={!item.done ? { scale: [1, 1.3, 1], opacity: [1, 0.6, 1] } : {}}
                              transition={{ repeat: Infinity, duration: 1.4 }}
                            />
                            <div>
                              <div className="timeline-text" style={{ color: item.done ? "var(--ink)" : "var(--ink-3)" }}>{item.label}</div>
                              <div className="timeline-sub">{item.sub}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* OPERATIONAL VIEW */}
              {activeTab === "operational" && (
                <motion.div
                  key="operational"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="apple-content"
                >
                  <div className="apple-header">
                    <div>
                      <h2>{activeLayer.title}</h2>
                      <p className="apple-desc">{activeLayer.desc}</p>
                    </div>
                    <div className="apple-badge" style={{ background: "#FEF7E0", color: "var(--amber)", borderColor: "rgba(244,194,75,0.2)" }}>{t.operations}</div>
                  </div>

                  <div className="info-panel">
                    {/* LEFT COL */}
                    <div className="info-col">
                      {/* Flow Pipeline */}
                      <div className="info-card" style={{ padding: 12 }}>
                        <h4>{t.flowPipeline}</h4>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", padding: "10px 0" }}>
                          <div style={{ position: "absolute", left: 16, right: 16, top: "40%", height: 2, background: "rgba(244,194,75,0.2)", zIndex: 1 }} />
                          {[
                            { name: t.reg, icon: "user", active: false },
                            { name: t.qms, icon: "users", active: true },
                            { name: t.billing, icon: "wallet", active: false },
                            { name: t.pharm, icon: "pill", active: false },
                          ].map((step, i) => (
                            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, zIndex: 2 }}>
                              <motion.div
                                animate={step.active ? { scale: [1, 1.15, 1], boxShadow: ["0 0 0 0px rgba(244,194,75,0.4)", "0 0 0 6px rgba(244,194,75,0.2)", "0 0 0 0px rgba(244,194,75,0.4)"] } : {}}
                                transition={{ repeat: Infinity, duration: 1.8 }}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  background: step.active ? "var(--amber)" : "#ffffff",
                                  border: `2px solid ${step.active ? "var(--amber)" : "rgba(11,23,51,0.08)"}`,
                                  color: step.active ? "#ffffff" : "var(--ink-3)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: step.active ? "0 4px 10px rgba(244,194,75,0.3)" : "none"
                                }}
                              >
                                <Icon name={step.icon} width={12} height={12} />
                              </motion.div>
                              <span style={{ fontSize: 9, fontWeight: 700, color: step.active ? "var(--ink)" : "var(--ink-4)" }}>{step.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Live QMS Board */}
                      <div className="info-card" style={{ flex: 1, padding: 12 }}>
                        <h4>{t.qmsLive}</h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {[
                            { id: "A-108", dept: locale === "hi" ? "OPD 02 (डॉ. सेन)" : "OPD 02 (Dr. Sen)", status: t.consulting, color: "var(--amber)", bg: "#FEF7E0" },
                            { id: "A-109", dept: locale === "hi" ? "OPD 05 (डॉ. राय)" : "OPD 05 (Dr. Ray)", status: t.nextIn, color: "var(--ink-3)", bg: "var(--bg-2)" },
                            { id: "P-402", dept: locale === "hi" ? "फार्मेसी C1" : "Pharmacy C1", status: t.ready, color: "var(--mint-dark)", bg: "var(--mint-soft)" },
                          ].map((ticket, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", justifySelf: "stretch", justifyContent: "space-between", padding: "6px 8px", borderRadius: 8, border: "1px solid rgba(11,23,51,0.04)", background: "#ffffff" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 500, color: "var(--ink)" }}>{ticket.id}</span>
                                <span style={{ fontSize: 10.5, color: "var(--ink-3)", fontWeight: 500 }}>{ticket.dept}</span>
                              </div>
                              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 999, background: ticket.bg, color: ticket.color }}>{ticket.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COL */}
                    <div className="info-col">
                      {/* Bed Management Map */}
                      <div className="info-card" style={{ flex: 1, padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <h4>{t.ipdBeds}</h4>
                          <span style={{ fontSize: 9, fontWeight: 700, background: "#FEF7E0", color: "var(--amber)", padding: "1px 5px", borderRadius: 4 }}>{t.wardA}</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, margin: "auto 0" }}>
                          {Array.from({ length: 8 }).map((_, idx) => {
                            const isOccupied = idx !== 5;
                            return (
                              <motion.div
                                key={idx}
                                whileHover={{ scale: 1.08 }}
                                style={{
                                  height: 32,
                                  borderRadius: 6,
                                  background: isOccupied ? "rgba(244,194,75,0.12)" : "var(--mint-soft)",
                                  border: `1px solid ${isOccupied ? "rgba(244,194,75,0.3)" : "rgba(184,232,201,0.4)"}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative"
                                }}
                              >
                                <Icon name="bed" width={12} height={12} style={{ color: isOccupied ? "var(--amber)" : "var(--mint-dark)" }} />
                                {isOccupied && (
                                  <span style={{ position: "absolute", top: 2, right: 2, width: 4, height: 4, borderRadius: "50%", background: "var(--amber)" }} />
                                )}
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Smart Billing Ledger */}
                      <div className="info-card" style={{ padding: 12 }}>
                        <h4>{t.billing}</h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10.5, color: "var(--ink-2)", fontFamily: "var(--font-mono)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>{t.consultationFee}</span>
                            <span style={{ fontWeight: 500 }}>₹500.00</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>{t.pharmacyDispense}</span>
                            <span style={{ fontWeight: 500 }}>₹320.00</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>{t.disposables}</span>
                            <span style={{ fontWeight: 500 }}>₹120.00</span>
                          </div>
                          <div style={{ height: 1, background: "rgba(11,23,51,0.06)", margin: "4px 0" }} />
                          <div style={{ display: "flex", justifyContent: "space-between", color: "var(--ink)", fontWeight: 600, fontSize: 11 }}>
                            <span>{t.unifiedTotal}</span>
                            <span style={{ color: "var(--amber)", fontWeight: 600 }}>₹940.00</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* INTELLIGENCE VIEW */}
              {activeTab === "intelligence" && (
                <motion.div
                  key="intelligence"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="apple-content"
                >
                  <div className="apple-header">
                    <div>
                      <h2>{activeLayer.title}</h2>
                      <p className="apple-desc">{activeLayer.desc}</p>
                    </div>
                    <div className="apple-badge" style={{ background: "var(--cyan-soft)", color: "var(--cyan-dark)", borderColor: "rgba(110,231,224,0.2)" }}>{t.aiAgent}</div>
                  </div>

                  <div className="info-panel">
                    {/* LEFT COL */}
                    <div className="info-col">
                      {/* Voice-MD & Lipi */}
                      <div className="info-card" style={{ padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <h4>{t.voiceLipi}</h4>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "#000000", background: "var(--cyan-soft)", padding: "1px 6px", borderRadius: 4 }}>{t.live}</span>
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                          {/* Voice-MD Column */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1.1 }}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <motion.div
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ repeat: Infinity, duration: 1.4 }}
                                style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--cyan-dark)", color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                              >
                                <Icon name="micro" width={10} height={10} />
                              </motion.div>
                              <div style={{ display: "flex", gap: 2, alignItems: "center", height: 12, flex: 1 }}>
                                {[14, 6, 18, 10, 15].map((h, i) => (
                                  <motion.div
                                    key={i}
                                    animate={{ height: [h * 0.4, h, h * 0.4] }}
                                    transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }}
                                    style={{ width: 1.8, background: "var(--cyan-dark)", borderRadius: 1 }}
                                  />
                                ))}
                              </div>
                            </div>
                            <div style={{ fontSize: 9, color: "var(--ink-3)", fontStyle: "italic", lineHeight: 1.2 }}>
                              "Tab. Paracetamol..."
                            </div>
                          </div>

                          {/* Divider */}
                          <div style={{ width: 1, background: "rgba(11,23,51,0.08)", alignSelf: "stretch" }} />

                          {/* Lipi AI Column */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 0.9 }}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <div style={{
                                width: 18,
                                height: 22,
                                borderRadius: 3,
                                border: "1px solid rgba(11,23,51,0.12)",
                                background: "#fff",
                                position: "relative",
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                padding: "3px 2px",
                                flexShrink: 0
                              }}>
                                <motion.div
                                  animate={{ top: ["5%", "85%", "5%"] }}
                                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    height: 1.2,
                                    background: "var(--cyan-dark)",
                                    boxShadow: "0 0 3px var(--cyan-dark)",
                                    zIndex: 2
                                  }}
                                />
                                <div style={{ height: 1.5, width: "80%", background: "rgba(11,23,51,0.12)", borderRadius: 0.5 }} />
                                <div style={{ height: 1.5, width: "60%", background: "rgba(11,23,51,0.12)", borderRadius: 0.5 }} />
                                <div style={{ height: 1.5, width: "70%", background: "rgba(11,23,51,0.12)", borderRadius: 0.5 }} />
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <span style={{ fontSize: 9.5, fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>Lipi AI</span>
                                <span style={{ fontSize: 7.5, color: "#2A9E5B", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#2A9E5B" }} />
                                  {t.ocrActive}
                                </span>
                              </div>
                            </div>
                            <div style={{ fontSize: 9, color: "var(--ink-3)", fontStyle: "italic", lineHeight: 1.2 }}>
                              Digitised Rx {"→"} HIMS
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sahyogi AI */}
                      <div className="info-card" style={{ flex: 1, padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <h4>{t.sahyogiChat}</h4>
                          <span style={{ fontSize: 8, color: "#000000", background: "var(--cyan-soft)", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>{t.live}</span>
                        </div>
                        
                        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, justifyContent: "space-between" }}>
                          {/* Message 1 */}
                          <div style={{ alignSelf: "flex-start", background: "var(--bg-2)", padding: "4px 8px", borderRadius: "8px 8px 8px 2px", fontSize: 8.5, color: "var(--ink-2)", maxWidth: "85%", lineHeight: 1.25 }}>
                            {t.sahyogiMsg1}
                          </div>
                          
                          {/* Message 2 */}
                          <div style={{ alignSelf: "flex-end", background: "var(--cyan-soft)", padding: "4px 8px", borderRadius: "8px 8px 2px 8px", fontSize: 8.5, color: "#000000", maxWidth: "85%", lineHeight: 1.25 }}>
                            {t.sahyogiMsg2}
                          </div>

                          {/* Message 3 */}
                          <div style={{ alignSelf: "flex-start", background: "var(--bg-2)", padding: "4px 8px", borderRadius: "8px 8px 8px 2px", fontSize: 8.5, color: "var(--ink-2)", maxWidth: "85%", lineHeight: 1.25 }}>
                            {t.sahyogiMsg3}
                          </div>

                          {/* Message 4 */}
                          <div style={{ alignSelf: "flex-end", background: "var(--cyan-soft)", padding: "4px 8px", borderRadius: "8px 8px 2px 8px", fontSize: 8.5, color: "#000000", maxWidth: "85%", lineHeight: 1.25 }}>
                            {t.sahyogiMsg4}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COL */}
                    <div className="info-col">
                      {/* ClaimIQ & Coding AI */}
                      <div className="info-card" style={{ flex: 1, padding: 10, display: "flex", flexDirection: "column", gap: 4, justifySelf: "stretch" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <h4 style={{ margin: 0 }}>ClaimIQ</h4>
                          <span style={{ fontSize: 8.5, fontWeight: 800, color: "#2A9E5B", background: "rgba(42, 158, 91, 0.12)", padding: "1.5px 5px", borderRadius: 4 }}>
                            {t.risk}
                          </span>
                        </div>

                        {/* Patient info context */}
                        <div style={{ background: "rgba(5, 60, 97, 0.02)", border: "1px solid rgba(11,23,51,0.03)", borderRadius: 8, padding: "5px 7px", marginBottom: 4, display: "flex", flexDirection: "column", gap: 1.5 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, fontWeight: 700, color: "#053C61" }}>
                            <span>{t.ramesh}</span>
                            <span style={{ color: "rgba(5, 60, 97, 0.6)" }}>TPA #182</span>
                          </div>
                          <div style={{ fontSize: 8, color: "rgba(5, 60, 97, 0.6)", fontWeight: 500 }}>
                            {t.icuStar}
                          </div>
                        </div>

                        {/* Pre-flight verified check items */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, justifyContent: "center" }}>
                          {[
                            { label: t.identityCheck, val: t.aadhaarResolved, badge: t.verified },
                            { label: t.procedureMatch, val: t.icdMapped, badge: t.validated },
                          ].map((item, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                              <span style={{ color: "#2A9E5B", marginTop: 1, display: "flex" }}><Icon name="check" width={9} height={9} /></span>
                              <div style={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                  <div style={{ fontSize: 8, color: "var(--ink-4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.01em" }}>{item.label}</div>
                                  <div style={{ fontSize: 9.5, color: "var(--ink-2)", fontWeight: 500, lineHeight: 1.15 }}>{item.val}</div>
                                </div>
                                <span style={{ fontSize: 7, fontWeight: 700, color: "#2A9E5B", background: "rgba(42,158,91,0.08)", padding: "1px 3px", borderRadius: 3 }}>{item.badge}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Vaani AI Video Consult */}
                      <div className="info-card" style={{ padding: "8px 10px", flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <h4 style={{ margin: 0 }}>Vaani</h4>
                          <span style={{ fontSize: 8, color: "#000000", background: "var(--cyan-soft)", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>{t.liveTranslation}</span>
                        </div>
                        <div style={{ fontSize: 8, color: "var(--ink-3)", marginTop: -4, marginBottom: 0, fontWeight: 500, lineHeight: 1.1 }}>
                          {t.vaaniDesc}
                        </div>
                        
                        {/* Two webcam streams */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, height: 60, marginTop: 1 }}>
                          {/* Doctor Feed */}
                          <div
                            style={{
                              background: "rgba(5, 60, 97, 0.03)",
                              borderRadius: 8,
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${speaker === "doctor" ? "var(--cyan-dark)" : "rgba(5, 60, 97, 0.05)"}`,
                              transition: "border 0.3s ease",
                            }}
                          >
                            <div style={{ color: speaker === "doctor" ? "var(--cyan-dark)" : "rgba(5, 60, 97, 0.4)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                              <Icon name="user" width={16} height={16} />
                              <span style={{ fontSize: 8, fontWeight: 700, color: "var(--ink-2)" }}>{t.drSharma}</span>
                            </div>
                            {speaker === "doctor" && (
                              <div style={{ position: "absolute", bottom: 4, right: 4, display: "flex", gap: 1, height: 5 }}>
                                <div className="voice-bar" style={{ width: 1, height: 3, background: "var(--cyan-dark)", animation: "voice-wave-1 0.6s infinite" }} />
                                <div className="voice-bar" style={{ width: 1, height: 5, background: "var(--cyan-dark)", animation: "voice-wave-2 0.8s infinite" }} />
                              </div>
                            )}
                          </div>

                          {/* Patient Feed */}
                          <div
                            style={{
                              background: "rgba(5, 60, 97, 0.03)",
                              borderRadius: 8,
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${speaker === "patient" ? "var(--cyan-dark)" : "rgba(5, 60, 97, 0.05)"}`,
                              transition: "border 0.3s ease",
                            }}
                          >
                            <div style={{ color: speaker === "patient" ? "var(--cyan-dark)" : "rgba(5, 60, 97, 0.4)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                              <Icon name="user" width={16} height={16} />
                              <span style={{ fontSize: 8, fontWeight: 700, color: "var(--ink-2)" }}>{t.patient}</span>
                            </div>
                            {speaker === "patient" && (
                              <div style={{ position: "absolute", bottom: 4, right: 4, display: "flex", gap: 1, height: 5 }}>
                                <div className="voice-bar" style={{ width: 1, height: 4, background: "var(--cyan-dark)", animation: "voice-wave-1 0.7s infinite" }} />
                                <div className="voice-bar" style={{ width: 1, height: 5, background: "var(--cyan-dark)", animation: "voice-wave-2 0.9s infinite 0.1s" }} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Subtitles Overlay */}
                        <div
                          style={{
                            background: "rgba(5, 60, 97, 0.02)",
                            border: "1px solid rgba(110,231,224,0.15)",
                            padding: "6px 10px",
                            borderRadius: 6,
                            fontSize: 8.5,
                            color: "var(--ink)",
                            minHeight: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            fontWeight: 500,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                            lineHeight: 1.25,
                          }}
                        >
                          {speaker === "patient" ? (
                            <span>
                              {t.patient}: <strong style={{ fontWeight: 600 }}>"नमस्ते डॉक्टर, बुखार है।"</strong>{" "}
                              <span style={{ color: "rgba(5, 60, 97, 0.55)", fontSize: 7.5, fontWeight: 500 }}>("Hello Doctor, I have a fever.")</span>
                            </span>
                          ) : (
                            <span>
                              {t.drSharma}: <strong style={{ fontWeight: 600 }}>"ठीक है, खांसी है?"</strong>{" "}
                              <span style={{ color: "rgba(5, 60, 97, 0.55)", fontSize: 7.5, fontWeight: 500 }}>("Okay, do you have a cough?")</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TRUST VIEW */}
              {activeTab === "trust" && (
                <motion.div
                  key="trust"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="apple-content"
                >
                  <div className="apple-header">
                    <div>
                      <h2>{activeLayer.title}</h2>
                      <p className="apple-desc">{activeLayer.desc}</p>
                    </div>
                    <div className="apple-badge" style={{ background: "rgba(5, 60, 97, 0.05)", color: "var(--ink)", borderColor: "rgba(5, 60, 97, 0.15)" }}>{t.encrypted}</div>
                  </div>

                  <div className="info-panel">
                    {/* LEFT COL */}
                    <div className="info-col">
                      {/* Compliance Checklist */}
                      <div className="info-card" style={{ padding: 12 }}>
                        <h4>{t.gateways}</h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                          {[
                            { label: t.abdmGateway, desc: t.hieCmActive, val: t.connected },
                            { label: "NHCX / PMJAY", desc: t.claimsTunnel, val: t.synced },
                            { label: t.fhirSchema, desc: t.hl7Formats, val: t.compliant },
                          ].map((c, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink)" }}>{c.label}</div>
                                <div style={{ fontSize: 9.5, color: "var(--ink-4)" }}>{c.desc}</div>
                              </div>
                              <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: "rgba(5, 60, 97, 0.05)", color: "var(--ink)" }}>{c.val}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* RBAC Visualizer */}
                      <div className="info-card" style={{ flex: 1, padding: 12 }}>
                        <h4>{t.rbacTitle}</h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                          {[
                            { user: t.drSen, role: t.doctor, action: t.writeEmr, status: t.granted, color: "var(--ink)", bg: "rgba(5, 60, 97, 0.05)" },
                            { user: t.staffRoy, role: locale === "hi" ? "बिलिंग" : "Billing", action: t.clinicalRx, status: t.restricted, color: "var(--rose)", bg: "var(--rose-soft)" },
                          ].map((a, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", justifySelf: "stretch", justifyContent: "space-between", padding: "5px 8px", borderRadius: 8, border: "1px solid rgba(11,23,51,0.04)", background: "#ffffff" }}>
                              <div>
                                <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink)" }}>{a.user} <span style={{ fontSize: 9, color: "var(--ink-4)", fontWeight: 500 }}>({a.role})</span></div>
                                <div style={{ fontSize: 9, color: "var(--ink-3)" }}>{a.action}</div>
                              </div>
                              <span style={{ fontSize: 8.5, fontWeight: 600, padding: "2px 5px", borderRadius: 4, background: a.bg, color: a.color }}>{a.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COL */}
                    <div className="info-col">
                      {/* Tamper-Proof Audit Logs Terminal */}
                      <div className="info-card" style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <h4>{t.ledgerTitle}</h4>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--ink)", fontSize: 9.5, fontWeight: 600 }}>
                            <Icon name="lock" width={10} height={10} /> {t.secure}
                          </div>
                        </div>
                        
                        <div style={{
                          background: "#111827",
                          borderRadius: 8,
                          padding: "10px 12px",
                          fontFamily: "var(--font-mono)",
                          fontSize: 9.5,
                          color: "#34d399",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                          boxShadow: "inset 0 2px 8px rgba(0,0,0,0.4)",
                          overflow: "hidden",
                          flex: 1
                        }}>
                          {[
                            { event: "ABDM_CONSENT", detail: `req_88f9 -> ${t.grantedRaw}`, time: "10:42:15" },
                            { event: "PAYLOAD_ENC", detail: "AES-256-GCM -> OK", time: "10:42:18" },
                            { event: "FHIR_BUNDLE", detail: `Patient -> ${t.syncedRaw}`, time: "10:42:21" },
                            { event: "LEDGER_WRITE", detail: "hash=0x9f8b...8c2a", time: "10:42:24" },
                            { event: "MFA_VERIFY", detail: `${locale === "hi" ? "डॉ. सेन" : "Dr. Sen"} -> ${t.successRaw}`, time: "10:42:27" },
                            { event: "ACCESS_LOG", detail: `${locale === "hi" ? "EMR_पठन मरीज_9921" : "EMR_READ Patient_9921"}`, time: "10:42:30" },
                          ].map((log, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -6 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.15 }}
                              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              <span style={{ color: "#6b7280", marginRight: 6 }}>{`[${log.time}]`}</span>
                              <span style={{ color: "#a7f3d0" }}>{log.event}</span>: {log.detail}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
        </div>
      </div>
    </>
  );
}
